import React from 'react';
import PropTypes from 'prop-types';

import Message from '@decisiv/ui-components/lib/components/Message';
import Select from '@decisiv/ui-components/lib/components/Select';
import TextField from '@decisiv/ui-components/lib/components/TextField';

import StepHeader from './StepHeader';
import { DETAILS_STEP } from './copy';
import { POPOVER_Z_INDEX } from './layers';
import {
  FieldGroup,
  PhoneCodeColumn,
  PhoneField,
  PhoneNumberColumn,
  PhoneRow,
  StepBody,
} from './styles';

/**
 * Step 2: who the user is.
 *
 * The two duplicate checks are what this step exists for — the ticket's whole
 * point is preventing a second record for somebody who already exists — so a
 * match shows the mockup's warning and blocks CONTINUE (see useCreateUserForm).
 *
 * There is no Username field, and no GPS or External Visibility fields: the
 * design collects none of them. The username is derived from the email server
 * side and reported on the success panel.
 */
function UserDetailsStep({
  checks,
  draft,
  jobRoleOptions,
  onBlurEmail,
  onBlurMobile,
  onChange,
  phoneCountryCodeOptions,
  serverMessages,
}) {
  const handle = (field) => (event) => onChange(field, event.target.value);

  // A rejected create beats a blur check: it is the more recent word on the field,
  // and for the mobile it may be a rule the blur check does not cover (a Decisiv
  // outbound number, say). Format is reported before duplication — a malformed
  // number cannot meaningfully be somebody else's.
  const emailWarning =
    serverMessages.email ||
    (checks.emailTaken ? DETAILS_STEP.emailTaken : undefined);

  const mobileWarning =
    serverMessages.mobile ||
    (!checks.mobileValid && DETAILS_STEP.mobileInvalid) ||
    (checks.mobileTaken ? DETAILS_STEP.mobileTaken : undefined);

  return (
    <StepBody>
      <StepHeader
        showRequiredKey
        subtitle={DETAILS_STEP.subtitle}
        title={DETAILS_STEP.title}
      />
      <FieldGroup>
        <TextField
          required
          label={DETAILS_STEP.firstName}
          name="firstName"
          value={draft.firstName}
          warningMessage={serverMessages.firstName}
          withWarningIntent={Boolean(serverMessages.firstName)}
          onChange={handle('firstName')}
        />
        <TextField
          required
          label={DETAILS_STEP.lastName}
          name="lastName"
          value={draft.lastName}
          warningMessage={serverMessages.lastName}
          withWarningIntent={Boolean(serverMessages.lastName)}
          onChange={handle('lastName')}
        />
        <Select
          helpMessage={DETAILS_STEP.jobRoleHelp}
          label={DETAILS_STEP.jobRole}
          name="jobRole"
          options={jobRoleOptions}
          value={draft.jobRole}
          // Without this the dropdown renders under the wizard — see ./layers.
          zIndex={POPOVER_Z_INDEX}
          onChange={(value) => onChange('jobRole', value)}
        />
        <TextField
          helpMessage={
            serverMessages.dmsId ? undefined : DETAILS_STEP.dmsIdHelp
          }
          label={DETAILS_STEP.dmsId}
          name="dmsId"
          value={draft.dmsId}
          warningMessage={serverMessages.dmsId}
          withWarningIntent={Boolean(serverMessages.dmsId)}
          onChange={handle('dmsId')}
        />
      </FieldGroup>
      {/* The mockup splits this step into two Form Sections — who the user is,
          then how to reach them — 20px apart, with 10px inside each. StepBody's
          own gap supplies the 20. */}
      <FieldGroup>
        <TextField
          required
          label={DETAILS_STEP.email}
          name="email"
          type="email"
          value={draft.email}
          warningMessage={emailWarning}
          withWarningIntent={Boolean(emailWarning)}
          onBlur={onBlurEmail}
          onChange={handle('email')}
        />
        <PhoneField>
          <PhoneRow>
            <PhoneCodeColumn>
              <Select
                hideClearButton
                label={DETAILS_STEP.countryCode}
                name="mobileCountryCode"
                options={phoneCountryCodeOptions}
                value={draft.mobileCountryCode}
                // Both halves of the pair go amber together, as the mockup draws
                // them: the code is as likely to be the wrong part as the number.
                // Select spreads its props onto InputField, so this reaches it.
                withWarningIntent={Boolean(mobileWarning)}
                zIndex={POPOVER_Z_INDEX}
                onChange={(value) => onChange('mobileCountryCode', value)}
              />
            </PhoneCodeColumn>
            <PhoneNumberColumn>
              <TextField
                label={DETAILS_STEP.mobile}
                name="mobile"
                type="tel"
                value={draft.mobile}
                withWarningIntent={Boolean(mobileWarning)}
                onBlur={onBlurMobile}
                onChange={handle('mobile')}
              />
            </PhoneNumberColumn>
          </PhoneRow>
          {/* Beneath the whole row rather than inside the number field, so it
              starts at the row's left edge as in the mockup. Message is what
              InputField renders for these itself, so they look the same. */}
          {mobileWarning ? (
            <Message intent="warning">{mobileWarning}</Message>
          ) : (
            <Message intent="help">{DETAILS_STEP.mobileHelp}</Message>
          )}
        </PhoneField>
      </FieldGroup>
    </StepBody>
  );
}

const optionsShape = PropTypes.arrayOf(
  PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
);

UserDetailsStep.propTypes = {
  /** What the server said on blur about the email and the mobile. */
  checks: PropTypes.shape({
    emailTaken: PropTypes.bool,
    mobileTaken: PropTypes.bool,
    mobileValid: PropTypes.bool,
  }),
  draft: PropTypes.shape({
    dmsId: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    jobRole: PropTypes.string,
    lastName: PropTypes.string,
    mobile: PropTypes.string,
    mobileCountryCode: PropTypes.string,
  }).isRequired,
  jobRoleOptions: optionsShape,
  onBlurEmail: PropTypes.func.isRequired,
  onBlurMobile: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  phoneCountryCodeOptions: optionsShape,
  /** Field messages from a rejected create, keyed by draft field. */
  serverMessages: PropTypes.objectOf(PropTypes.string),
};

UserDetailsStep.defaultProps = {
  checks: { emailTaken: false, mobileTaken: false, mobileValid: true },
  jobRoleOptions: [],
  phoneCountryCodeOptions: [],
  serverMessages: {},
};

export default UserDetailsStep;
